"use client";

import { useState, type ReactNode } from "react";

import { EdgenModal } from "@components/EdgenModal";
import { InviteCodeEntryRow } from "./InviteCodeEntryRow";
import type { InviteCodeEntryPage } from "./inviteCodeEvents";
import { InviteCodeSheet } from "./InviteCodeSheet";

/**
 * 「入口行 + 填码弹层」组合 —— 起始页与登录页共用，避免两处各写一遍开关逻辑。
 *
 * 弹层是用户点击触发、同页覆盖的局部层（不进导航栈，已填邮箱不丢），
 * 不属于 Surface Manager 管理的全局主动弹层（fe-spec §3.3.4）。
 */
export function useInviteCodeEntry({
  page,
  tone,
  variant,
  locale,
  className,
}: {
  page: InviteCodeEntryPage;
  tone?: "on_dark" | "on_light";
  variant?: "inline" | "stacked";
  locale?: string;
  className?: string;
}): { entryRow: ReactNode; sheet: ReactNode } {
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);

  const entryRow = (
    <InviteCodeEntryRow
      page={page}
      tone={tone}
      variant={variant}
      locale={locale}
      className={className}
      // or-label: n/a —— 只是透传回调，label 打在 InviteCodeEntryRow 的 button 上
      onClick={() => setOpen(true)}
    />
  );

  const sheet = (
    <EdgenModal
      open={open}
      onClose={close}
      footer={null}
      closable={false}
      maskClosable
      swipeToClose
      // H5 底部全宽、顶部圆角 24；PC 居中 400px、圆角 12（Figma 110013:130019 / 131088）
      className="[--invite-sheet-radius:24px_24px_0_0] [--invite-sheet-width:100%] md:[--invite-sheet-radius:12px] md:[--invite-sheet-width:400px]"
      contentStyle={{
        borderRadius: "var(--invite-sheet-radius)",
        width: "var(--invite-sheet-width)",
        padding: 0,
        overflow: "hidden",
      }}
      containerStyle={{ maxHeight: "none", overflowY: "visible" }}
    >
      <InviteCodeSheet open={open} onClose={close} locale={locale} />
    </EdgenModal>
  );

  return { entryRow, sheet };
}
